import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Box, Typography, TextField } from '@mui/material';
import { Theme } from '@mui/material/styles';
import BusinessIcon from '@mui/icons-material/Business';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { CampaignProspectType } from '../../api/campaigns';

interface CampaignBreadcrumbProps {
  prospectType: CampaignProspectType;
  campaignName: string;
  onCampaignNameChange?: (newName: string) => void;
  /** When true, campaign name is not editable (read-only). */
  readOnly?: boolean;
  /** Darker campaign title + separator on tinted header; "Campaigns" stays `themePrimary` (link). */
  onTintedHeader?: boolean;
}

const CampaignBreadcrumb: React.FC<CampaignBreadcrumbProps> = ({
  prospectType,
  campaignName,
  onCampaignNameChange,
  readOnly = false,
  onTintedHeader = false
}) => {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [localCampaignName, setLocalCampaignName] =
    useState<string>(campaignName);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setLocalCampaignName(campaignName);
  }, [campaignName]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const iconStyles = {
    fontSize: '20px',
    color: (theme: Theme) => theme.customColors.themePrimary,
    flexShrink: 0
  };

  const ProspectIcon =
    prospectType === CampaignProspectType.ACCOUNT
      ? BusinessIcon
      : AssignmentIcon;

  const handleCampaignNameClick = () => {
    if (readOnly) return;
    setIsEditing(true);
  };

  const handleCampaignNameChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setLocalCampaignName(event.target.value);
  };

  const handleCampaignNameBlur = () => {
    setIsEditing(false);
    if (readOnly) return;
    const trimmedName = localCampaignName.trim();
    if (trimmedName) {
      setLocalCampaignName(trimmedName);
      if (onCampaignNameChange) {
        onCampaignNameChange(trimmedName);
      }
    } else {
      setLocalCampaignName(campaignName);
    }
  };

  const handleCampaignNameKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      handleCampaignNameBlur();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      setLocalCampaignName(campaignName);
      setIsEditing(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        ml: 0,
        gap: 0,
        flexShrink: 0
      }}
    >
      <ProspectIcon sx={iconStyles} />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
        <Typography
          component={Link}
          to="/campaign"
          sx={{
            fontSize: '15px',
            fontWeight: 500,
            color: (theme) => theme.customColors.themePrimary,
            cursor: 'pointer',
            padding: '4px 4px',
            borderRadius: '4px',
            transition: 'background-color 0.2s ease',
            textDecoration: 'none',
            '&:hover': {
              backgroundColor: onTintedHeader
                ? 'rgba(0, 0, 0, 0.06)'
                : 'rgba(0, 0, 0, 0.04)'
            }
          }}
        >
          Campaigns
        </Typography>
        <Typography
          sx={{
            fontSize: '15px',
            color: (theme) =>
              onTintedHeader
                ? theme.palette.grey[600]
                : theme.palette.text.secondary,
            mx: 0.25
          }}
        >
          /
        </Typography>
        {isEditing ? (
          <TextField
            inputRef={inputRef}
            value={localCampaignName}
            onChange={handleCampaignNameChange}
            onBlur={handleCampaignNameBlur}
            inputProps={{ onKeyDown: handleCampaignNameKeyDown }}
            variant="standard"
            sx={{
              '& .MuiInput-root': {
                fontSize: '15px',
                fontWeight: 500,
                color: (theme) =>
                  onTintedHeader
                    ? theme.customColors.campaignHeaderTitleColor
                    : theme.customColors.themeNeutralPrimaryColor,
                '&:before': {
                  borderBottom: '2px solid',
                  borderBottomColor: (theme: Theme) =>
                    theme.customColors.themePrimary || '#1976d2'
                },
                '&:hover:not(.Mui-disabled):before': {
                  borderBottom: '2px solid',
                  borderBottomColor: (theme: Theme) =>
                    theme.customColors.themePrimary || '#1976d2'
                },
                '&:after': {
                  borderBottom: '2px solid',
                  borderBottomColor: (theme: Theme) =>
                    theme.customColors.themePrimary || '#1976d2'
                }
              },
              '& .MuiInput-input': {
                padding: '4px 0',
                minWidth: '200px'
              }
            }}
          />
        ) : (
          <Typography
            onClick={handleCampaignNameClick}
            sx={{
              fontSize: '15px',
              fontWeight: onTintedHeader ? 600 : 500,
              color: (theme) =>
                onTintedHeader
                  ? theme.customColors.campaignHeaderTitleColor
                  : theme.customColors.themeNeutralPrimaryColor,
              cursor: readOnly ? 'default' : 'pointer',
              padding: '4px 8px',
              borderRadius: '4px',
              transition: 'background-color 0.2s ease',
              '&:hover': readOnly
                ? {}
                : {
                    backgroundColor: onTintedHeader
                      ? 'rgba(0, 0, 0, 0.06)'
                      : 'rgba(0, 0, 0, 0.04)'
                  }
            }}
          >
            {localCampaignName}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default CampaignBreadcrumb;
